import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { translations } from "./translations";
import { getGeoLang } from "../lib/api";

const LangContext = createContext({ lang: "en", setLang: () => {} });
const STORAGE_KEY = "ybl-lang";

function readParam() {
  try {
    const p = new URLSearchParams(window.location.search).get("lang");
    return p === "it" || p === "en" ? p : null;
  } catch {
    return null;
  }
}

function readSaved() {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s === "it" || s === "en" ? s : null;
  } catch {
    return null;
  }
}

function browserLang() {
  const nav = (typeof navigator !== "undefined" && navigator.language) || "en";
  return nav.toLowerCase().startsWith("it") ? "it" : "en";
}

function detectInitial() {
  return readParam() || readSaved() || browserLang();
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitial);
  // Explicit = user/URL chose a language; suppresses IP-based auto-detection.
  const explicit = useRef(Boolean(readParam() || readSaved()));

  const setLang = useCallback((l) => {
    explicit.current = true;
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* localStorage unavailable */ }
  }, []);

  // Persist a ?lang= choice so it sticks across navigation.
  useEffect(() => {
    const p = readParam();
    if (p) { try { localStorage.setItem(STORAGE_KEY, p); } catch { /* localStorage unavailable */ } }
  }, []);

  // Country-based auto-detection (only when the visitor hasn't chosen explicitly).
  useEffect(() => {
    if (explicit.current) return;
    let active = true;
    getGeoLang()
      .then((d) => {
        if (!active || explicit.current || !d?.lang) return;
        setLangState(d.lang);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);

export function useT() {
  const { lang } = useLang();
  return useCallback(
    (key) => {
      const dict = translations[lang] || translations.en;
      return dict[key] ?? translations.en[key] ?? key;
    },
    [lang]
  );
}
